import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify } from "ra-core";

import type { CrmDataProvider } from "../providers/types";
import type { MailAccount } from "./mailTypes";
import { MailError } from "./mailTypes";

export const MAIL_ACCOUNT_QUERY_KEY = ["mailAccount"];

/** The current user's mailbox and calendar connection. */
export const useMailAccount = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  return useQuery({
    queryKey: MAIL_ACCOUNT_QUERY_KEY,
    queryFn: () => dataProvider.getMailAccount(),
    // Not saved for offline use: it changes with each connection test.
    meta: { persist: false },
  });
};

/** Translation key of a mail error code sent by the server. */
export const mailErrorKey = (error: unknown): string =>
  `crm.mail.errors.${error instanceof MailError ? error.code : "unknown"}`;

/**
 * Runs an account change, keeps the cached account in step and reports the
 * outcome: `successMessage` on success, the translated error otherwise.
 */
export const useMailAccountMutation = <Input>(
  mutationFn: (
    dataProvider: CrmDataProvider,
    input: Input,
  ) => Promise<MailAccount>,
  successMessage: string,
) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const notify = useNotify();
  return useMutation({
    mutationFn: (input: Input) => mutationFn(dataProvider, input),
    onSuccess: (account) => {
      queryClient.setQueryData(MAIL_ACCOUNT_QUERY_KEY, account);
      notify(successMessage, { type: "success" });
    },
    onError: (error) => {
      notify(mailErrorKey(error), { type: "error" });
    },
  });
};
